'use client';
import { Fragment, useEffect, useMemo, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import dynamic from 'next/dynamic';
import { usePathname, useRouter } from 'next/navigation';
import { useTranslations as useTranslations$1 } from 'next-intl';
import { useAuth } from '@/hooks/useAuth';
import { useBillingInfo } from '@/hooks/useBillingInfo';
import { useMemberships } from '@/hooks/useMemberships';
import { useWorkspaceContext } from '@/store/workspaceContextStore';
import { AccountDropdown, SignUpModal } from '@/components/auth';
import { FeedbackButton } from '@/components/FeedbackButton';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Check, ChevronDown, LayoutGrid, LibraryIcon, PlayCircle, Plus, Settings, Workflow } from '@/components/ui/icons';
import { useActionsAccessStatus } from '@/hooks/useActionsAccess';
import { navPill } from '@/components/ui/nav-pill';
import { OliveIcon } from '@/components/icons';
import { formatOlives } from '@/lib/constants';
import { isEnterpriseDeployment } from '@/lib/deployment';
import { useLibraryEnabled } from '@/lib/libraryFlag';
import { cn } from '@/lib/utils';
import { resolveWorkspaceChip, teamSlugFromPath } from '@/lib/workspaceChrome';
import { PERSONAL_WORKSPACE_ID, workspaceSwitchHref } from '@/lib/workspaceNavigation';
import { explicitWorkspaceHref, parseWorkspaceRoute } from '@/lib/workspaceRoutes';
// Route-specific extras, code-split so their weight (Fuse index, Bowser) doesn't
// land in the shared chunk now that the header renders app-wide from the layout.
const LearnSearch = dynamic(()=>import('@/components/learn/LearnSearch').then((m)=>({
            default: m.LearnSearch
        })), {
    ssr: false
});
const OpenInDesktopApp = dynamic(()=>import('@/components/OpenInDesktopApp').then((m)=>({
            default: m.OpenInDesktopApp
        })), {
    ssr: false
});
export interface AppHeaderCrumb {
    label: string;
    /** When set, the crumb is a link. The final (current) crumb usually omits this. */ href?: string;
}
export interface AppHeaderConfig {
    /**
   * Breadcrumb trail shown after the logomark, e.g. `[{ label: 'Billing' }]` or
   * `[{ label: 'Learn', href: '/learn' }, { label: 'Glossary' }]`. The logomark
   * always links home, so an empty array renders just the brand.
   */ breadcrumbs?: AppHeaderCrumb[];
    /**
   * `solid` (default) is the standard app-chrome bar. `onMedia` is the
   * transparent, white-tinted variant for full-screen flows layered over
   * video/black (welcome, plans, invites).
   */ tone?: 'solid' | 'onMedia';
    /** Show the Learn fuzzy-search box in the center slot (Learn pages). */ search?: boolean;
    /** Show the "Open in desktop app" control before the cluster (projects). */ desktopApp?: boolean;
    /** Stick to the top of the viewport while scrolling. Default true. */ sticky?: boolean;
    /**
   * `full` (default) is the standard chrome. `focused` strips navigation
   * affordances for the onboarding funnel: static logomark (no /projects
   * link), no Learn button, and the account menu reduced to identity,
   * language, and log out. Feedback stays — it opens a modal, not a
   * navigation.
   */ chrome?: 'full' | 'focused';
    className?: string;
}
/**
 * The workspace switcher — WHOSE world you're in. One active context
 * (Personal or a team), scoping both /projects and /library. Admin for a
 * context lives behind its gear; "Create Team" lives at the bottom.
 */ function WorkspaceSwitcher() {
    const t = useTranslations$1();
    const router = useRouter();
    const pathname = usePathname();
    const { memberships, isLoading: membershipsLoading } = useMemberships();
    const { billingInfo } = useBillingInfo();
    const contextId = useWorkspaceContext((s)=>s.contextId);
    const hydrated = useWorkspaceContext((s)=>s.hydrated);
    const pendingContextId = useWorkspaceContext((s)=>s.pendingContextId);
    const beginSwitch = useWorkspaceContext((s)=>s.beginSwitch);
    const requestCreateTeam = useWorkspaceContext((s)=>s.requestCreateTeam);
    const [open, setOpen] = useState(false);
    // Apply the persisted context after mount (SSR and first client render both
    // show 'personal', so hydrating later never mismatches).
    useEffect(()=>{
        useWorkspaceContext.getState().hydrate();
    }, []);
    const teams = useMemo(()=>memberships?.teamOrgs ?? [], [
        memberships?.teamOrgs
    ]);
    const workspaceRoute = parseWorkspaceRoute(pathname);
    const { activeTeam, resolving, teamSlug } = resolveWorkspaceChip({
        pathname,
        contextId,
        hydrated,
        pendingContextId,
        memberships,
        membershipsLoading
    });
    const presentedContextId = pendingContextId === null && workspaceRoute?.destination.kind === 'personal' ? PERSONAL_WORKSPACE_ID : contextId;
    const personalBalance = billingInfo?.personalOrg?.balance;
    const teamBalance = (id: string)=>billingInfo?.teamOrgs?.find((team)=>team.id === id)?.balance;
    // Same olive grammar as the account dropdown: icon left, mono digits.
    const balanceChip = (balance: number | null | undefined)=>typeof balance === 'number' ? (<span className="flex shrink-0 items-center gap-1 font-mono text-xs text-muted-foreground/70">
        <OliveIcon className="size-3"/>
        {formatOlives(balance, {
            floor: true
        })}
      </span>) : null;
    // Exact: anything BELOW /team/<slug>/projects falls through to the team-admin
    // catch-all (which dropped its projects tab), so a prefix match would call it
    // the projects surface and skip the navigation Create Team needs.
    const onProjectsSurface = workspaceRoute?.surface === 'projects';
    // Every row's destination is a plain `router.push` target, not a <Link>, so
    // nothing ever prefetches it. Warming them all when the menu opens is what
    // turns the switch from a blocking RSC fetch into a fast commit (ENG-723).
    useEffect(()=>{
        if (!open) return;
        router.prefetch(workspaceSwitchHref(pathname, PERSONAL_WORKSPACE_ID, null));
        for (const team of teams)router.prefetch(workspaceSwitchHref(pathname, team.id, team.slug));
    }, [
        open,
        router,
        pathname,
        teams
    ]);
    const pick = (id: string)=>{
        setOpen(false);
        const href = workspaceSwitchHref(pathname, id, teams.find((x)=>x.id === id)?.slug);
        // Already here — nothing to navigate to, and nothing to cover. Guarding on
        // the href (not on `id === contextId`) matters both ways: a re-pick of the
        // current workspace from an admin page still navigates to its home, and a
        // pick that resolves back to this very page (a team with no slug) doesn't
        // raise an overlay that no route commit would ever take down.
        if (href === pathname) return;
        // Adopt the picked workspace immediately (the chip renames) and mark the
        // navigation in flight, so AppShell covers the workspace-scoped content
        // until the destination route commits. Without that mark the previous
        // workspace's projects and olive balance sit under the new workspace's
        // name for however long the navigation takes.
        beginSwitch(id, href);
        router.push(href);
    };
    return (<Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button aria-busy={resolving || undefined} aria-label={resolving ? t("SWg5nU") : undefined} className="flex min-w-0 items-center gap-1.5 rounded-md px-1.5 py-1 transition-colors hover:bg-secondary">
          { /* Until the workspace can be named, the chip holds its space rather
              than claiming "Personal" — on a team link that would be a wrong
              answer the user watches get corrected. When the fetch fails
              outright the URL's slug is the last honest name we have: uglier
              than the team's display name, but still the right workspace. */ }
          {resolving ? (<span className="h-4 w-24 animate-pulse rounded bg-muted motion-reduce:animate-none"/>) : (<span className="max-w-40 truncate text-sm font-medium text-foreground">
              {activeTeam ? activeTeam.name : (teamSlug ?? t("NDx-B0"))}
            </span>)}
          <ChevronDown className="size-3.5 shrink-0 text-muted-foreground"/>
        </button>
      </PopoverTrigger>
      { /* Same menu dialect as every other dropdown in the app: plain text
          rows, quiet meta on the right, no avatar chrome. */ }
      <PopoverContent align="start" className="w-60 p-1">
        { /* A Personal-only list mid-fetch reads as "you have no teams" — the
            same lie as the chip. Hold the shape until the real list lands. */ }
        {resolving ? (<div aria-busy className="flex flex-col gap-1.5 px-2 py-2">
            {[
        0,
        1,
        2
    ].map((row)=>(<span key={row} className="h-3.5 animate-pulse rounded bg-muted motion-reduce:animate-none"/>))}
          </div>) : (<>
            { /* Check lives in a fixed LEFT gutter (macOS-menu grammar) so the
                active mark never drifts with row content; Create Team's +
                shares the same gutter. */ }
            <button onClick={()=>pick(PERSONAL_WORKSPACE_ID)} className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm text-foreground hover:bg-secondary">
              <Check className={cn('size-3.5 shrink-0', presentedContextId === PERSONAL_WORKSPACE_ID ? 'opacity-100' : 'opacity-0')}/>
              <span className="min-w-0 flex-1 truncate">{t("NDx-B0")}</span>
              {balanceChip(personalBalance)}
            </button>
            {teams.map((team)=>(<button key={team.id} onClick={()=>pick(team.id)} className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm text-foreground hover:bg-secondary">
                <Check className={cn('size-3.5 shrink-0', presentedContextId === team.id ? 'opacity-100' : 'opacity-0')}/>
                <span className="min-w-0 flex-1 truncate">{team.name}</span>
                {balanceChip(teamBalance(team.id))}
              </button>))}
          </>)}
        <div className="my-1 border-t border-border"/>
        <button onClick={()=>{
        setOpen(false);
        requestCreateTeam();
        // The dialog lives on the projects page, which serves both routes —
        // so go to the CURRENT workspace's list. Hardcoding /projects here
        // would land on a URL the restore then bounces back off (ENG-725),
        // remounting the page and dropping the pending request.
        if (!onProjectsSurface) {
            router.push(activeTeam?.slug ? explicitWorkspaceHref({
                kind: 'team',
                slug: activeTeam.slug
            }, 'projects') : explicitWorkspaceHref({
                kind: 'personal'
            }, 'projects'));
        }
    }} className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm text-muted-foreground hover:bg-secondary hover:text-foreground">
          <Plus className="size-3.5 shrink-0"/>
          {t("97DGta")}
        </button>
      </PopoverContent>
    </Popover>);
}
const TONE = {
    solid: {
        // Same chrome as the editor's top bar (h-12 bg-card) so moving between
        // canvas and the workspace surfaces doesn't repaint the navbar.
        bar: 'border-border bg-card',
        sep: 'text-muted-foreground/40',
        link: 'text-muted-foreground hover:text-foreground',
        current: 'text-foreground'
    },
    onMedia: {
        bar: 'border-white/10 bg-transparent backdrop-blur-sm',
        sep: 'text-white/30',
        link: 'text-white/70 hover:text-white',
        current: 'text-white'
    }
} as const;
/**
 * The single top bar shared by every authenticated, non-editor route (projects,
 * billing/account/settings, learn, team, lineage, and the onboarding flows).
 *
 * On the standard app sections it is rendered once by `AppShell` (in the app
 * layout) and configured per-route via `useAppHeader`, so it stays mounted across
 * navigation instead of flashing. The onboarding flows render it inline (over
 * their video background) with `tone="onMedia"`. The editor keeps its own chrome
 * (Vesper TopBar) and is intentionally excluded.
 */ export function AppHeader({ breadcrumbs = [], tone = 'solid', search = false, desktopApp = false, sticky = true, chrome = 'full', className }: AppHeaderConfig) {
    const { auth, isAnonymous } = useAuth();
    const libraryEnabled = useLibraryEnabled();
    // Workflows is part of the Actions prototype: the tab shows only for
    // allowlisted accounts, like Action cards on the canvas.
    const workflowsEnabled = useActionsAccessStatus(isAnonymous ? null : (auth?.userId ?? null)) === true;
    const pathname = usePathname();
    const [showSignUpModal, setShowSignUpModal] = useState(false);
    const t = useTranslations$1();
    const toneStyle = TONE[tone];
    const focused = chrome === 'focused';
    // Learn is a global nav affordance for signed-in users — shown on every route
    // except Learn itself and the focused onboarding chrome.
    const onLearn = pathname === '/learn' || (pathname?.startsWith('/learn/') ?? false);
    const showLearn = !onLearn && !!auth?.profile && !isAnonymous && !focused;
    // The workspace's two nouns — Projects (the work) and Library (what the work
    // produces and reuses) — read as ONE top-left toggle on those roots, replacing
    // the breadcrumb label. Elsewhere the breadcrumb trail renders as usual.
    // Exact, for the same reason as the switcher's copy: a deeper /team/<slug>/…
    // path is the team-admin page, and should read as admin here too.
    const workspaceRoute = parseWorkspaceRoute(pathname);
    const onProjects = workspaceRoute?.surface === 'projects';
    const onLibrary = workspaceRoute?.surface === 'library';
    const onWorkflows = workspaceRoute?.surface === 'workflows';
    const onTeamSurface = !!workspaceRoute && workspaceRoute.destination.kind === 'team';
    // Admin pages keep the workspace navbar too — you're one click from
    // Projects/Library, and the switcher chip names the workspace you're
    // managing (a team's settings, or your own account/billing/settings).
    const onTeamAdmin = !onTeamSurface && teamSlugFromPath(pathname) !== null;
    const onUserAdmin = [
        '/settings'
    ].some((p)=>pathname === p || (pathname?.startsWith(`${p}/`) ?? false));
    const workspaceToggle = !focused && (onProjects || onLibrary || onWorkflows || onTeamAdmin || onUserAdmin);
    // The Settings tab manages the ACTIVE workspace: a team's admin page when a
    // team context is selected, your own account pages for Personal.
    const { memberships } = useMemberships();
    const headerContextId = useWorkspaceContext((s)=>s.contextId);
    // On a team surface the URL already names the workspace, so these links stay
    // inside it from the first paint instead of pointing home to Personal for as
    // long as /memberships takes to answer.
    const explicitPersonal = workspaceRoute?.destination.kind === 'personal';
    const activeTeamSlug = explicitPersonal ? null : (teamSlugFromPath(pathname) ?? memberships?.teamOrgs?.find((team)=>team.id === headerContextId)?.slug ?? null);
    const settingsHref = activeTeamSlug ? `/team/${activeTeamSlug}` : '/settings/account';
    const projectsHref = activeTeamSlug ? explicitWorkspaceHref({
        kind: 'team',
        slug: activeTeamSlug
    }, 'projects') : explicitWorkspaceHref({
        kind: 'personal'
    }, 'projects');
    const libraryHref = activeTeamSlug ? explicitWorkspaceHref({
        kind: 'team',
        slug: activeTeamSlug
    }, 'library') : explicitWorkspaceHref({
        kind: 'personal'
    }, 'library');
    const workflowsHref = activeTeamSlug ? explicitWorkspaceHref({
        kind: 'team',
        slug: activeTeamSlug
    }, 'workflows') : explicitWorkspaceHref({
        kind: 'personal'
    }, 'workflows');
    return (<>
      <header className={cn('flex h-12 items-center gap-4 border-b px-4', sticky && 'sticky top-0 z-50', toneStyle.bar, className)}>
        { /* Left — home-linked logomark + breadcrumb trail */ }
        <div className="flex min-w-0 items-center gap-2.5">
          {focused ? (<span className="flex shrink-0 items-center">
              { /* eslint-disable-next-line i18next/no-literal-string -- brand */ }
              <Image src="/whitemartini.svg" alt="Martini" width={19} height={18} priority/>
            </span>) : (<Link href={projectsHref} aria-label={t("Z938Yh")} className="flex shrink-0 items-center transition-opacity hover:opacity-80">
              { /* eslint-disable-next-line i18next/no-literal-string -- brand */ }
              <Image src="/whitemartini.svg" alt="Martini" width={19} height={18} priority/>
            </Link>)}
          { /* Which DEPLOYMENT you're on — the dedicated enterprise instance is
              visually identical to martini.film, so name it in the chrome. */ }
          {isEnterpriseDeployment() && (<span className={cn('hidden select-none rounded border px-1.5 py-px text-[10px] font-medium uppercase tracking-wider lg:inline-flex', tone === 'onMedia' ? 'border-white/20 text-white/70' : 'border-border text-muted-foreground')}>
              { /* i18n-ignore -- deployment label, kept verbatim */ }
              { /* eslint-disable-next-line i18next/no-literal-string -- deployment label, kept verbatim */ }
              {'Enterprise'}
            </span>)}
          {workspaceToggle && <WorkspaceSwitcher/>}
          { /* Same pill grammar as the editor's Create / Edit mode switcher. */ }
          {workspaceToggle && (<nav className="ml-3 flex items-center gap-0.5">
              <Link href={projectsHref} className={navPill(onProjects)}>
                <LayoutGrid className="h-3.5 w-3.5"/>
                {t("UxTJRa")}
              </Link>
              {libraryEnabled && (<Link href={libraryHref} className={navPill(onLibrary)}>
                  <LibraryIcon className="h-3.5 w-3.5"/>
                  {t("StcK67")}
                </Link>)}
              {(workflowsEnabled || onWorkflows) && (<Link href={workflowsHref} className={navPill(onWorkflows)}>
                  <Workflow className="h-3.5 w-3.5"/>
                  { /* i18n-ignore -- Actions prototype surface; the sweep wraps it when it ships */ }
                  { /* eslint-disable-next-line i18next/no-literal-string -- prototype surface */ }
                  Workflows
                </Link>)}
              { /* Settings = manage the ACTIVE workspace: a team's admin page,
                  or your own account/billing for the personal context. */ }
              <Link href={settingsHref} className={navPill(onTeamAdmin || onUserAdmin)}>
                <Settings className="h-3.5 w-3.5"/>
                {t("D3idYv")}
              </Link>
            </nav>)}
          {!workspaceToggle && breadcrumbs.map((crumb, i)=>{
        // The final crumb is the current page: always rendered white and
        // non-interactive, so the section label matches across every route.
        // Earlier crumbs are muted, and links when an href is given.
        const isLast = i === breadcrumbs.length - 1;
        return (<Fragment key={`${crumb.label}-${i}`}>
                  <span className={cn('select-none', toneStyle.sep)}>/</span>
                  {crumb.href && !isLast ? (<Link href={crumb.href} className={cn('truncate text-sm font-medium transition-colors', toneStyle.link)}>
                      {crumb.label}
                    </Link>) : (<span className={cn('truncate text-sm font-medium', isLast ? toneStyle.current : toneStyle.link)}>
                      {crumb.label}
                    </span>)}
                </Fragment>);
    })}
        </div>

        { /* Center — optional Learn search */ }
        {search ? (<div className="flex flex-1 justify-center">
            <LearnSearch/>
          </div>) : (<div className="flex-1"/>)}

        { /* Right — page action, then the standard Learn + Support + account cluster */ }
        <div className="flex shrink-0 items-center gap-2">
          {desktopApp && <OpenInDesktopApp/>}
          {showLearn && (<Button variant="ghost" size="sm" asChild className="gap-1.5 text-muted-foreground hover:text-foreground">
              <Link href="/learn" target="_blank" rel="noopener noreferrer">
                <PlayCircle className="h-4 w-4"/>
                <span className="text-sm">{t("IbrSk1")}</span>
              </Link>
            </Button>)}
          <FeedbackButton/>
          {auth?.profile && (<AccountDropdown alias={auth.profile.alias} color={auth.profile.color} email={auth.profile.email} isAnonymous={isAnonymous} onSignUpClick={()=>setShowSignUpModal(true)} menu={focused ? 'focused' : 'full'}/>)}
        </div>
      </header>

      {showSignUpModal && (<SignUpModal isOpen onClose={()=>setShowSignUpModal(false)} returnPath={pathname ?? '/projects'}/>)}
    </>);
}
